/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { API } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';

import {
  allBidsByVendor,
  allJobs,
  bidSubmissionsByVendorId,
  getAllBids,
  getAllJobs,
  jobInterests,
  recommendedJobs,
} from '../../../graphql/queries';
import { BidQueryType, JobsQueryType } from '../../../API';
import { AuthHeader, AWSAuthMode } from '../../../Shared/Constants/App';
import { getWebInfo, signedFetch } from '../../../Shared/Utilities/utils';
import { IGraphQLResponse } from '../../../Shared/Models';
import { AuthenticationService } from '../../../Shared/Services/AuthenticationService';
import awsConfig from '../../../Configuration/environment-config';

/** Appends every truthy value as a query param, skipping the empty/undefined ones. */
const appendQueryParams = (url: URL, params: Record<string, string | number | undefined>): void => {
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url?.searchParams?.append(key, String(value));
    }
  });
};

export default class MarketplaceApiService {
  static async getRecommendedJobs(
    vendorId: string,
    dueDate?: string,
    limit?: number,
    pageNumber?: number,
    sortBy?: string,
    sortOrder?: string,
    searchText?: string
  ) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const response: IGraphQLResponse = await API.graphql(
        {
          query: recommendedJobs,
          variables: { vendorId, dueDate, limit, pageNumber, sortBy, sortOrder, searchText },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );

      let modifiedResponse: {
        matchedJobs?: any;
      } = {};

      const metadata = response.data?.recommendedJobs?.matchedJobs?.metadata;

      if (response.data?.recommendedJobs) {
        modifiedResponse = response.data?.recommendedJobs;
        modifiedResponse.matchedJobs = response.data?.recommendedJobs?.matchedJobs?.data;
      }

      return { data: modifiedResponse ?? {}, metadata: metadata, errors: [] };
    } catch (e: any) {
      return { data: [], metadata: [], errors: e.errors };
    }
  }

  static async getBidsInProgress(
    vendorId: string,
    pageNumber?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: string
  ) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const bidsByVendorResponse: IGraphQLResponse = await API.graphql(
        {
          query: allBidsByVendor,
          variables: { vendorId, pageNumber, limit, sortBy, sortOrder },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      let modifiedResponse: {
        matchedFacilities?: any;
        matchedJobs?: any;
        matchedBids?: any;
      } = {};
      if (bidsByVendorResponse?.data?.allBidsByVendor) {
        modifiedResponse = bidsByVendorResponse?.data?.allBidsByVendor;
      }
      return { data: modifiedResponse ?? {}, errors: [] };
    } catch (e: any) {
      return { data: null, errors: e.errors };
    }
  }

  static async getAllJobs(
    vendorId: string,
    sortBy: string,
    sortOrder: string,
    pageNumber: number,
    limit: number,
    dueDate?: string
  ) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const response: IGraphQLResponse = await API.graphql(
        {
          query: allJobs,
          variables: { vendorId, sortBy, sortOrder, pageNumber, limit, dueDate },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      const metadata = response.data?.allJobs?.matchedJobs?.metadata;
      let modifiedResponse: {
        matchedFacilities?: any;
        matchedJobs?: any;
      } = {};

      if (response.data?.allJobs) {
        modifiedResponse = response.data?.allJobs;
        modifiedResponse.matchedJobs = response.data?.allJobs?.matchedJobs?.data;
      }

      return {
        data: modifiedResponse ?? {},
        metadata: metadata,
        errors: [],
      };
    } catch (e: any) {
      return { data: null, errors: e.errors };
    }
  }

  static async getNotInterestedJobs(customerId: string, vendorId: string, sortBy?: string, sortOrder?: string) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const allJobInterestsResponse: IGraphQLResponse = await API.graphql(
        {
          query: jobInterests,
          variables: { customerId, vendorId, sortBy, sortOrder },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: allJobInterestsResponse.data?.jobInterests ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async submittedBids(vendorId: string, pageNumber?: number, limit?: number) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const response: IGraphQLResponse = await API.graphql(
        {
          query: bidSubmissionsByVendorId,
          variables: { vendorId, pageNumber, limit },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );

      const modifiedResponse: any = {};
      if (response.data?.bidSubmissionsByVendorId) {
        modifiedResponse.matchedJobsMetaData = response.data?.bidSubmissionsByVendorId?.matchedJobs?.metadata;
        modifiedResponse.matchedJobs = response.data?.bidSubmissionsByVendorId?.matchedJobs?.data;
        modifiedResponse.matchedBids = response.data?.bidSubmissionsByVendorId?.matchedBids?.data;
        modifiedResponse.matchedBidsMetaData = response.data?.bidSubmissionsByVendorId?.matchedBids?.metadata;
        modifiedResponse.matchedFacilities = response.data?.bidSubmissionsByVendorId?.matchedFacilities;
      }
      return { data: modifiedResponse ?? {}, errors: [] };
    } catch (e: any) {
      return { data: null, errors: e.errors };
    }
  }

  static async getAllBids(
    vendorId: string,
    type?: BidQueryType,
    pageNumber?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: string,
    searchColumnName?: string,
    searchText?: string,
    dueDate?: string
  ) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const response: IGraphQLResponse = await API.graphql(
        {
          query: getAllBids,
          variables: { vendorId, type, limit, pageNumber, sortBy, sortOrder, dueDate, searchColumnName, searchText },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: response.data?.getAllBids?.data ?? {}, metadata: response.data?.getAllBids?.metadata, errors: [] };
    } catch (e: any) {
      return { data: [], metadata: [], errors: e.errors };
    }
  }

  static async AllBids(
    vendorId: string,
    userId: string,
    type?: BidQueryType,
    pageNumber?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: string,
    searchColumnName?: string,
    searchText?: string,
    dueDate?: string
  ) {
    try {
      const endpointConfig = awsConfig?.aws_cloud_logic_custom?.find((api) => api?.name === 'getAllBids');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(endpointConfig?.endpoint);
      appendQueryParams(url, {
        vendorId,
        userId,
        type,
        pageNumber,
        limit,
        sortBy,
        sortOrder,
        searchColumnName,
        searchText,
        dueDate,
      });

      const response = await signedFetch({
        method: 'GET',
        url: url.toString(),
      });
      return {
        data: response?.data?.data ?? [],
        metadata: response?.data?.metadata ?? {},
        errors: response?.errors ?? [],
        status: response?.data?.status ?? null,
      };
    } catch (e: any) {
      console.error(`error`, e);
    }
  }

  static async AllJobs(
    vendorId: string,
    type?: JobsQueryType,
    pageNumber?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: string,
    searchColumnName?: string,
    searchText?: string,
    dueDate?: string
  ) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const response: IGraphQLResponse = await API.graphql(
        {
          query: getAllJobs,
          variables: { vendorId, type, limit, pageNumber, sortBy, sortOrder, dueDate, searchColumnName, searchText },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: response.data?.getAllJobs?.data ?? {}, metadata: response.data?.getAllJobs?.metadata, errors: [] };
    } catch (e: any) {
      return { data: [], metadata: [], errors: e.errors };
    }
  }

  static async GetAllJobs(
    vendorId: string,
    type?: JobsQueryType,
    pageNumber?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: string,
    searchColumnName?: string,
    searchText?: string,
    dueDate?: string,
    userId?: string
  ) {
    try {
      const endpointConfig = awsConfig?.aws_cloud_logic_custom?.find((api) => api?.name === 'getAllJobs');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(endpointConfig?.endpoint);
      appendQueryParams(url, {
        vendorId,
        type,
        pageNumber,
        limit,
        sortBy,
        sortOrder,
        searchColumnName,
        searchText,
        dueDate,
      });

      const response = await signedFetch({
        method: 'GET',
        url: url?.toString(),
        requestId: uuidv4(),
        userId,
      });
      return {
        data: response?.data?.data ?? [],
        metadata: response?.data?.metadata ?? {},
        errors: response?.errors ?? [],
        status: response?.data?.status ?? null,
      };
    } catch (e: any) {
      console.error(`error`, e);
    }
  }
}
